import { Component, OnInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Router } from '@angular/router';
import { DecimalPipe, NgForOf, NgIf } from '@angular/common';
import {
  TuiButtonModule,
  TuiLoaderModule,
  TuiSvgModule,
  TuiTextfieldControllerModule,
  TuiHintModule,
} from '@taiga-ui/core';
import {
  TuiDataListWrapperModule,
  TuiInputDateRangeModule,
  TuiInputModule,
  TuiPaginationModule,
  TuiSelectModule,
  TuiComboBoxModule,
} from '@taiga-ui/kit';
import { TuiTableModule } from '@taiga-ui/addon-table';
import {
  TuiStringifyContentPipeModule,
  TuiStringifyPipeModule,
  TuiFilterByInputPipeModule,
} from '@taiga-ui/kit';
import { FormsModule } from '@angular/forms';
import { TuiStringHandler, TuiValueChangesModule } from '@taiga-ui/cdk';
import { TuiDay, TuiDayRange } from '@taiga-ui/cdk';

import { PageSize } from '../../core/dto/page.size';
import { Pagination } from '../../core/dto/pagination';
import { PaymentService } from '../../core/services/payment.service';
import { AuthService } from '../../modules/auth/services/auth.service';
import { MemberService } from '../../core/services/member.service';
import { StatusBadgeComponent } from '../../core/dto/status-badge.component';

@Component({
  selector: 'app-payments',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    FormsModule,
    DecimalPipe,
    TuiButtonModule,
    TuiLoaderModule,
    TuiPaginationModule,
    TuiSvgModule,
    TuiTableModule,
    TuiTextfieldControllerModule,
    TuiHintModule,
    TuiInputDateRangeModule,
    TuiInputModule,
    TuiSelectModule,
    TuiComboBoxModule,
    TuiDataListWrapperModule,
    TuiValueChangesModule,
    TuiStringifyContentPipeModule,
    TuiStringifyPipeModule,
    TuiFilterByInputPipeModule,
    StatusBadgeComponent,
  ],
  providers: [PaymentService],
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
})
export class PaymentsComponent implements OnInit {
  paymentData: any[] = [];
  members: any[] = [];
  user: any;

  pageSize = new PageSize();
  isLoading = false;
  showSearch = false;
  statusMsg = false;

  // Filters
  searchText: string | null = null;
  tempSearchText = '';
  memberFilter: any = null;
  paymentMethodFilter: string | null = null;
  dateFilter: TuiDayRange | null = null;
  dateTypeSelect: string | null = 'Payment Date';

  /** Current month for table view (payment date filter). Defaults to current month. */
  selectedMonth: { year: number; month: number } = { year: 0, month: 0 };
  /** Fixed reference to the real current month, used to prevent navigating into the future. */
  private readonly currentMonthRef: { year: number; month: number } = {
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
  };

  // Sorts
  validFromSort = -1;
  validToSort = -1;
  paymentDateSort = -1;
  sortType = 'paymentDate';

  // Totals
  totalCash = 0;
  totalOnline = 0;
  totalCombined = 0;
  allFilteredTotals = { cash: 0, online: 0, combined: 0 };
  sendingReport = false;
  reportMessage: string | null = null;

  paymentMethodOptions: readonly string[] = ['Online', 'Cash'];
  dateType = ['Validity Period', 'Payment Date'];

  /** Tooltip text explaining the date filter types for the advanced search. */
  dateFilterTypeTooltip =
    'Validity Period: filter by membership coverage dates (Valid From / Valid To). Payment Date: filter by when the payment was made.';

  stringify: TuiStringHandler<unknown> = (item: unknown): string => {
    const m = item as {
      membershipId?: string;
      firstname?: string;
      lastname?: string;
    };
    return `${m?.membershipId ?? ''} ${m?.firstname ?? ''} ${
      m?.lastname ?? ''
    }`.trim();
  };

  constructor(
    private router: Router,
    private paymentService: PaymentService,
    private authService: AuthService,
    private memberService: MemberService
  ) {}

  ngOnInit(): void {
    this.user = this.authService.getLoggedInUser();
    this.setMonthToCurrent();
    this.getPaginatedPayment();
    this.loadAllMembers();
  }

  setMonthToCurrent(): void {
    const now = new Date();
    this.selectedMonth = { year: now.getFullYear(), month: now.getMonth() };
    this.dateFilter = this.getMonthRange(this.selectedMonth.year, this.selectedMonth.month);
    this.dateTypeSelect = 'Payment Date';
  }

  private getMonthRange(year: number, month: number): TuiDayRange {
    const first = new TuiDay(year, month, 1);
    const lastDate = new Date(year, month + 1, 0).getDate();
    const last = new TuiDay(year, month, lastDate);
    return new TuiDayRange(first, last);
  }

  goToPreviousMonth(): void {
    if (this.selectedMonth.month === 0) {
      this.selectedMonth = { year: this.selectedMonth.year - 1, month: 11 };
    } else {
      this.selectedMonth = { ...this.selectedMonth, month: this.selectedMonth.month - 1 };
    }
    this.dateFilter = this.getMonthRange(this.selectedMonth.year, this.selectedMonth.month);
    this.pageSize.pageIndex = 1;
    this.getPaginatedPayment();
  }

  goToNextMonth(): void {
    // Prevent navigating beyond the real current month
    if (this.isNextMonthDisabled) {
      return;
    }

    if (this.selectedMonth.month === 11) {
      this.selectedMonth = { year: this.selectedMonth.year + 1, month: 0 };
    } else {
      this.selectedMonth = { ...this.selectedMonth, month: this.selectedMonth.month + 1 };
    }
    this.dateFilter = this.getMonthRange(this.selectedMonth.year, this.selectedMonth.month);
    this.pageSize.pageIndex = 1;
    this.getPaginatedPayment();
  }

  get selectedMonthLabel(): string {
    const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${names[this.selectedMonth.month]} ${this.selectedMonth.year}`;
  }

  get isNextMonthDisabled(): boolean {
    return (
      this.selectedMonth.year === this.currentMonthRef.year &&
      this.selectedMonth.month === this.currentMonthRef.month
    );
  }

  private buildFilters(extra: any = {}): any {
    const dateFilterForApi = this.normalizeDateFilterForApi(this.dateFilter);
    return Object.assign({}, new Pagination(this.pageSize), {
      filters: {
        validFromSort: this.validFromSort,
        validToSort: this.validToSort,
        paymentDateSort: this.paymentDateSort,
        sortType: this.sortType,
        searchValue: this.searchText,
        dateFilter: dateFilterForApi,
        dateType: this.dateTypeSelect,
        memberId: this.memberFilter?._id || null,
        paidBy: this.paymentMethodFilter ? [this.paymentMethodFilter] : null,
      },
      ...extra,
    });
  }

  /** Convert TuiDayRange to API format (ISO date strings) so backend can filter by payment date. */
  private normalizeDateFilterForApi(range: TuiDayRange | null): { from: string; to: string } | null {
    if (!range?.from || !range?.to) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    const from = `${range.from.year}-${pad(range.from.month + 1)}-${pad(range.from.day)}`;
    const to = `${range.to.year}-${pad(range.to.month + 1)}-${pad(range.to.day)}`;
    return { from, to };
  }

  async getPaginatedPayment(): Promise<void> {
    this.isLoading = true;
    try {
      const res = await this.paymentService.getPagedPayment(
        this.buildFilters()
      );
      this.paymentData = res.data;
      this.pageSize.length = res.totalPages;
      this.calculateTotals(this.paymentData);
      await this.calculateAllFilteredTotals();
    } finally {
      this.isLoading = false;
    }
    this.isLoading = false;
  }

  private calculateTotals(payments: any[]): void {
    let cash = 0,
      online = 0;
    for (const p of payments) {
      const method = Array.isArray(p.paidBy) && p.paidBy[0]?.toLowerCase();
      if (method === 'cash') cash += +p.amount || 0;
      if (method === 'online') online += +p.amount || 0;
    }
    this.totalCash = cash;
    this.totalOnline = online;
    this.totalCombined = cash + online;
  }

  private async calculateAllFilteredTotals(): Promise<void> {
    const res = await this.paymentService.getPagedPayment(
      this.buildFilters({ pageIndex: 1, pageSize: 10000 })
    );
    this.calculateTotals(res.data);
    this.allFilteredTotals = {
      cash: this.totalCash,
      online: this.totalOnline,
      combined: this.totalCombined,
    };
  }

  // --- Helpers ---
  formatDate(dateString: any): string {
    return new Date(dateString).toISOString().split('T')[0];
  }

  formatDateCSV(dateString: any): string {
    const d = new Date(dateString);
    return `" ${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}"`;
  }

  showPaymentMethod(p: any): string {
    const val = Array.isArray(p?.paidBy) && p.paidBy[0];
    if (!val) return '-';
    if (/online/i.test(val)) return 'Online';
    if (/cash/i.test(val)) return 'Cash';
    return val;
  }

  async sendDailyReportEmail(): Promise<void> {
    this.sendingReport = true;
    this.reportMessage = null;
    try {
      const res = await this.paymentService.sendDailyReportEmail();
      this.reportMessage = res?.message || 'Daily report email sent successfully.';
      this.statusMsg = true;
    } catch (err: any) {
      this.reportMessage = err?.message || 'Failed to send daily report email.';
    } finally {
      this.sendingReport = false;
    }
  }

  // --- Excel Export ---
  async downloadTableExcel(): Promise<void> {
    const allPayments = await this.paymentService
      .getPagedPayment(this.buildFilters({ pageIndex: 1, pageSize: 10000 }))
      .then((res) => res.data || []);

    let cash = 0,
      online = 0;
    allPayments.forEach((p: any) => {
      const method = Array.isArray(p.paidBy) && p.paidBy[0]?.toLowerCase();
      if (method === 'cash') cash += +p.amount || 0;
      if (method === 'online') online += +p.amount || 0;
    });

    const header = [
      'Payment ID',
      'Member Name',
      'Member ID',
      'Validity Period From',
      'Validity Period To',
      'Payment Date',
      'Amount (LKR)',
      'Payment Method',
      'Notes',
      'Status',
      'Payment Presets',
      'Staff Name',
    ];

    const rows = allPayments.map((p: any) => [
      p.paymentId || '-',
      p.member ? `${p.member.firstname} ${p.member.lastname}` : '-',
      p.member?.membershipId || '-',
      p.validFrom ? this.formatDateCSV(p.validFrom) : '-',
      p.validTo ? this.formatDateCSV(p.validTo) : '-',
      p.paymentDate ? this.formatDateCSV(p.paymentDate) : '-',
      p.amount || '-',
      this.showPaymentMethod(p),
      p.notes || '-',
      p.status || '-',
      p.paymentPresets || '-',
      p.staffName || '-',
    ]);

    const worksheetData = [
      ['Total Cash Received', cash],
      ['Total Online Received', online],
      ['Total Payments (Combined)', cash + online],
      [],
      header,
      ...rows,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      'payments_report.xlsx'
    );
  }

  // --- UI actions ---
  toggle(): void {
    const wasOpen = this.showSearch;
    this.showSearch = !this.showSearch;

    // When turning OFF advanced search, reset filters and data to defaults
    if (wasOpen && !this.showSearch) {
      this.resetStatus();
    }
  }

  toggleSort(field: 'validFrom' | 'validTo' | 'paymentDate'): void {
    if (field === 'validFrom') this.validFromSort *= -1;
    if (field === 'validTo') this.validToSort *= -1;
    if (field === 'paymentDate') this.paymentDateSort *= -1;
    this.sortType = field;
    this.getPaginatedPayment();
  }

  search(): void {
    this.statusMsg = true;
    this.tempSearchText = this.searchText ?? '';
    this.getPaginatedPayment();
  }

  cancelSearch(event: any): void {
    if (!event) {
      this.searchText = null;
      this.getPaginatedPayment();
    }
  }

  resetStatus(): void {
    this.searchText = null;
    this.setMonthToCurrent();
    this.memberFilter = null;
    this.paymentMethodFilter = null;
    this.statusMsg = false;
    this.tempSearchText = '';
    this.reportMessage = null;
    this.getPaginatedPayment();
  }

  goToPage(event: number): void {
    this.pageSize.pageIndex = event + 1;
    this.getPaginatedPayment();
  }

  // --- Navigation ---
  navigateToAdd(): void {
    this.router.navigate(['admin/payment/add-edit']);
  }

  navigateToUpdate(data: any): void {
    if (this.user.role !== 'USER') {
      this.router.navigate([`admin/payment/add-edit/${data._id}`]);
    }
  }

  navToSingleMember(id: string): void {
    this.router.navigate([`admin/view-member/${id}`]);
  }

  // --- Members ---
  loadAllMembers(): void {
    this.memberService.getAllMembers().then((res) => {
      this.members = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
    });
  }

  get tableMembers(): any[] {
    const seen = new Set();
    return this.paymentData
      .map((p) => p.member)
      .filter((m) => m && m._id && !seen.has(m._id) && seen.add(m._id));
  }

  showText(): string {
    return this.statusMsg && this.tempSearchText
      ? `Results for "${this.tempSearchText}"`
      : '';
  }

  getFilterSummary(): string {
    if (!this.statusMsg) {
      return '';
    }

    const parts: string[] = [];

    if (this.searchText) {
      parts.push(`Matching "${this.searchText}"`);
    }

    if (this.memberFilter) {
      const firstname = this.memberFilter.firstname || '';
      const lastname = this.memberFilter.lastname || '';
      const fullName = `${firstname} ${lastname}`.trim();
      const membershipId = this.memberFilter.membershipId;

      if (fullName || membershipId) {
        const memberLabel = [
          fullName
        ]
          .filter(Boolean)
          .join(' ');
        parts.push(`Member ${memberLabel}`);
      }
    }

    if (this.paymentMethodFilter) {
      const method =
        this.paymentMethodFilter.toLowerCase() === 'cash' ? 'cash' : 'online';
      parts.push(`Paid ${method}`);
    }

    if (this.dateFilter?.from && this.dateFilter?.to) {
      const from = this.formatDay(this.dateFilter.from);
      const to = this.formatDay(this.dateFilter.to);
      const label =
        this.dateTypeSelect === 'Validity Period'
          ? 'validity period'
          : 'payment date';
      parts.push(`With ${label} from ${from} to ${to}`);
    }

    if (parts.length === 0) {
      return 'Showing all payments';
    }

    return `Results for | ${parts.join(' | ')}`;
  }

  private formatDay(day: TuiDay): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${day.year}-${pad(day.month + 1)}-${pad(day.day)}`;
  }

  toggleSortValidFromDate() {
    this.toggleSort('validFrom');
  }
  toggleSortValidToDate() {
    this.toggleSort('validTo');
  }
  toggleSortPaymentDate() {
    this.toggleSort('paymentDate');
  }
}
